import { createSelector, createSlice } from "@reduxjs/toolkit";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, queryMapFactory } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";
import { logout } from "../common/reducers/initContext";

const key = eoi => eoi.id;

export const expressionOfInterestSlice = createSlice({
    name: 'expressionOfInterest',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload;
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(eoi => {
                const idx = state.data.findIndex(e => key(e) === key(eoi));
                if (idx >= 0) {
                    state.data.splice(idx, 1, eoi);
                } else {
                    state.data.push(eoi);
                }
            })
        },
        remove: (state, action) => {
            const toRemove = new Set(ensureArray(action.paylod).map(eoi => key(eoi)));
            state.data = state.data.filter(eoi => !toRemove.has(key(eoi)));
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => expressionOfInterestSlice.getInitialState())
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'eoi_one', action, data => ({ name: data.name, id: data.displayid }));
const visualizeFailure = verb => DefaultFailureAction(verb, 'eoi_one');

const fetchEOI = id => network => network.get('/api/v2/expressionOfInterest/' + encodeURIComponent(id), expressionOfInterestSlice.actions.addOrUpdate, visualizeFailure('get'));
const fetchUnproccessedExpressionsOfInterest = network => network.get('/api/v2/expressionsOfInterest', expressionOfInterestSlice.actions.set, visualizeFailure('get'));

export const createExpressionOfInterest = (network, interest) => network.post('/api/v2/expressionOfInterest', interest, visualizeSuccess('create', expressionOfInterestSlice.actions.addOrUpdate), visualizeFailure('create'));
export const updateExpressionOfInterest = (network, delta) => network.put('/api/v2/expressionOfInterest', delta, expressionOfInterestSlice.actions.addOrUpdate, visualizeFailure('update'));
export const processExpressionOfInterest = (network, action, id) => network.put(`/api/v2/processExpressionOfInterest/${encodeURIComponent(action)}`, { id }, visualizeSuccess('remove', expressionOfInterestSlice.actions.remove), visualizeFailure('remove'));
// #endregion

// #region selectors
const selectEOIData = state => state.expressionOfInterest.data;
const selectEOI = id => createSelector([selectEOIData], eois => eois.find(eoi => key(eoi) === id));
const selectUnprocessedInterest = createSelector([selectEOIData], eois => {
    return eois.filter(eoi => !eoi.accepted && !eoi.rejected)
});

const fullRoles = ['administrator', 'member manager'];
const readRoles = ['auditor', 'member auditor'];

export const canCreateEOI = (credentials, eoi) => eoi ? credentials.hasRole(fullRoles, eoi) : credentials.hasRole(fullRoles);
export const canUpdateEOI = canCreateEOI;
export const canRemoveEOI = canCreateEOI;
export const canRouteEOI = ({ credentials, clientConfig }) => credentials.hasRole(fullRoles.concat(readRoles)) && clientConfig.expressionOfInterest?.enable

// #endregion
const listen = {
    eoiCreated: (eoi, { dispatch, websocket }) => {
        markFetched(websocket, 'eoi', key(eoi));
        dispatch(expressionOfInterestSlice.actions.addOrUpdate(eoi))
    },
    eoiUpdated: (eoi, { dispatch }) => dispatch(expressionOfInterestSlice.actions.addOrUpdate(eoi)),
    eoiRemoved: (eoi, { dispatch }) => dispatch(expressionOfInterestSlice.actions.remove(eoi))
}
const cleanup = { root: expressionOfInterestSlice.name, all: state => selectEOIData(state), removeAll: expressionOfInterestSlice.actions.remove, key }
const eoiInitByID = initMapFactory(fetchEOI, listen);
registerEntityBasedFetch('eoi', eoiInitByID);
export const [useExpressionOfInterest] = dataHookFactory(selectEOI, eoiInitByID, { cleanup });
export const [useUnproccessedExpressionsOfInterest] = dataHookFactory(selectUnprocessedInterest, queryMapFactory(fetchUnproccessedExpressionsOfInterest, 'id', initMapFactory, listen, { allowEmpty: true })(), { cleanup });

const reducer = { name: expressionOfInterestSlice.name, reducer: expressionOfInterestSlice.reducer };
export default reducer;